/**
 * dst-showcase-track — one continuous pinned scroll track cycling through
 * every product in the homepage showcase, so it reads as a single piece
 * ("one wheel rotates to side, fades to black, fades in as another wheel")
 * rather than separate stacked scroll-jacked sections.
 *
 * One <dst-showcase-track> per section, one "reel" per product_showcase
 * block (`.dst-shcw__reel` elements — each doubles as both the reel's frame
 * sequence data and its CTA content).
 *
 * Per-reel five-segment cycle:
 *   Fade in    — opacity 0→1, frame held at 0
 *   Rotate in  — opacity 1, frame scrubs 0 → N-1
 *   Hold       — frame held at N-1 (full frontal); this reel's CTA becomes active
 *   Rotate out — frame scrubs N-1 → 0 (reverse of rotate in)
 *   Fade out   — opacity 1→0, frame held at 0
 * The fade-out of reel N and the fade-in of reel N+1 both pass through pure
 * black, which is what makes the handoff between products read as continuous.
 *
 * The FIRST reel skips fade-in/rotate-in entirely — it starts already at the
 * Hold frame, fully visible with its caption, so there's something engaging
 * on page load instead of a black screen the visitor has to scroll to
 * reveal. Its preload order is reversed to match (loads from the end of the
 * sequence first, since that's what's shown immediately).
 *
 * A reel with no frame sequence configured just holds its static fallback
 * image through fade-in/hold/fade-out (rotate segments have nothing to
 * animate) — content can land later without breaking the mechanic.
 *
 * Bails out entirely on mobile / prefers-reduced-motion — CSS shows a
 * separate, always-static, stacked-images fallback instead (see
 * sections/dst-homepage-showcase.liquid). Same for <noscript>.
 */
class DstShowcaseTrack extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(max-width: 749px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this._canvas      = this.querySelector('.dst-shcw__canvas');
    this._ctx         = this._canvas.getContext('2d');
    this._sticky      = this.querySelector('.dst-shcw__sticky');
    this._indicator   = this.querySelector('[data-loading]');
    this._progressEl  = this.querySelector('.dst-shcw__progress');
    this._scrollHint  = this.querySelector('.dst-shcw__scroll-hint');
    this._resizeTimer = null;
    this._currentReel = -1;
    this._currentFrame = -1;
    this._activeCta = -1;

    // Reel CTAs ship with `is-active` on the first one in the server-rendered
    // HTML purely as a no-JS fallback signal — strip it so JS has full control
    // from the start (same fix applied to dst-scroll-hero's chapters).
    this._reelEls = Array.from(this.querySelectorAll('.dst-shcw__reel'));
    this._reelEls.forEach(el => el.classList.remove('is-active'));

    this._reels = this._reelEls.map((el, i) => {
      const baseUrl = el.dataset.frameBaseUrl || '';
      const frameCount = baseUrl ? (parseInt(el.dataset.frameCount, 10) || 1) : 1;
      const fadeVh   = parseFloat(el.dataset.fadeVh) || 60;
      const rotateVh = parseFloat(el.dataset.rotateVh) || 200;
      const isFirst  = i === 0;
      return {
        baseUrl,
        ext:      el.dataset.frameExt || 'webp',
        pad:      parseInt(el.dataset.framePad, 10) || 3,
        fit:      el.dataset.fit || 'cover',
        frameCount,
        fallbackUrl: el.dataset.fallbackUrl || '',
        fallbackImg: null,
        // Every reel exits the same way (rotate out, fade out). Only the
        // first reel's entry is skipped — it opens already at Hold.
        fadeInVh:    isFirst ? 0 : fadeVh,
        rotateInVh:  isFirst ? 0 : rotateVh,
        holdVh:      parseFloat(el.dataset.holdVh) || 150,
        rotateOutVh: rotateVh,
        fadeOutVh:   fadeVh,
        reversed: isFirst, // preload from the end of the sequence first
        frames: new Array(frameCount),
        loaded: new Set(),
      };
    });

    if (this._reels.length === 0) return;

    this.classList.add('dst-shcw--js-ready');
    this._resizeCanvas();

    const first = this._reels[0];
    this._loadReelStart(first, () => {
      this._currentReel  = 0;
      this._currentFrame = first.frameCount - 1; // opens at Hold, not frame 0
      this._drawReelFrame(0, this._currentFrame);
      this._scrollGate = new DstScrollUtils.ScrollGate(this, () => this._onScrollRAF());
      this._onScrollRAF(); // correct opacity/frame immediately if scroll position was restored (back/forward, refresh)
      this._startBackgroundLoad();
    });

    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this._resizeCanvas(), 150);
    }, { passive: true });
  }

  disconnectedCallback() {
    if (this._scrollGate) this._scrollGate.destroy();
  }

  // ── Reel start / loading ─────────────────────────────────────────────────────

  // Which frame indices to preload first: the start of the sequence normally,
  // or the end of it for a reversed (first) reel — that's what's shown first.
  _initialBatchRange(reel) {
    const n = Math.min(30, reel.frameCount);
    return reel.reversed
      ? { from: reel.frameCount - n, to: reel.frameCount }
      : { from: 0, to: n };
  }

  // Loads whatever's needed to paint a reel's very first frame — either the
  // relevant batch of sequence frames, or just the fallback image if there's
  // no sequence configured for this reel yet.
  _loadReelStart(reel, callback) {
    if (!reel.baseUrl) {
      this._loadFallback(reel).then(callback);
      return;
    }
    const { from, to } = this._initialBatchRange(reel);
    this._preloadRange(reel, from, to, callback);
  }

  _loadFallback(reel) {
    return new Promise(resolve => {
      if (reel.fallbackImg || !reel.fallbackUrl) { resolve(); return; }
      const img = new Image();
      img.onload  = () => { reel.fallbackImg = img; resolve(); };
      img.onerror = () => resolve();
      img.src = reel.fallbackUrl;
    });
  }

  _frameUrl(reel, index) {
    const n = String(index + 1).padStart(reel.pad, '0');
    return `${reel.baseUrl}${n}.${reel.ext}`;
  }

  _loadFrame(reel, index) {
    return new Promise(resolve => {
      if (reel.loaded.has(index)) { resolve(); return; }
      const img = new Image();
      img.onload  = () => { reel.loaded.add(index); resolve(); };
      img.onerror = () => { resolve(); };
      img.src = this._frameUrl(reel, index);
      reel.frames[index] = img;
    });
  }

  _preloadRange(reel, from, to, callback) {
    let remaining = to - from;
    if (remaining <= 0) { callback(); return; }
    for (let i = from; i < to; i++) {
      this._loadFrame(reel, i).then(() => { if (--remaining === 0) callback(); });
    }
  }

  // Loads every remaining frame across every reel at once, with several
  // requests in flight concurrently, instead of finishing one reel's entire
  // sequence before even starting the next — same fix, same reason, as
  // dst-scroll-hero.js: enough reels × frames that a visitor scrolling at a
  // normal pace could reach a reel before it had even started loading.
  //
  // Priority order: every reel's fallback image (cheap, always useful) and
  // its two "immediately visible" frames — index 0 (shown at fade-in) and
  // the last frame (shown at Hold) — so _drawNearest always has something
  // close by even before the rest of that reel has loaded. Then every
  // remaining frame, reel by reel.
  _startBackgroundLoad() {
    const queue = [];
    const queuedFrames = new Map(); // reel -> Set(indices already queued)

    const queueFrame = (reel, index) => {
      if (reel.loaded.has(index)) return;
      let set = queuedFrames.get(reel);
      if (!set) { set = new Set(); queuedFrames.set(reel, set); }
      if (set.has(index)) return;
      set.add(index);
      queue.push({ type: 'frame', reel, index });
    };

    for (const reel of this._reels) {
      if (reel.fallbackUrl && !reel.fallbackImg) queue.push({ type: 'fallback', reel });
    }
    for (const reel of this._reels) {
      if (!reel.baseUrl) continue;
      queueFrame(reel, 0);
      queueFrame(reel, reel.frameCount - 1);
    }
    for (const reel of this._reels) {
      if (!reel.baseUrl) continue;
      for (let i = 0; i < reel.frameCount; i++) queueFrame(reel, i);
    }

    const CONCURRENCY = 6;
    let cursor = 0;
    const pullNext = () => {
      if (cursor >= queue.length) return;
      const item = queue[cursor++];
      const job = item.type === 'fallback' ? this._loadFallback(item.reel) : this._loadFrame(item.reel, item.index);
      job.then(pullNext);
    };
    for (let i = 0; i < CONCURRENCY; i++) pullNext();
  }

  // ── Canvas ────────────────────────────────────────────────────────────────────

  _resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    this._cw = this._canvas.clientWidth;
    this._ch = this._canvas.clientHeight;
    this._canvas.width  = this._cw * dpr;
    this._canvas.height = this._ch * dpr;
    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._drawReelFrame(this._currentReel, this._currentFrame);
  }

  _drawReelFrame(reelIndex, frameIndex) {
    const reel = this._reels[reelIndex];
    if (!reel) return;

    if (!reel.baseUrl) {
      if (reel.fallbackImg) {
        this._setLoading(false);
        this._paint(reel.fallbackImg, reel.fit);
      } else {
        this._setLoading(true);
      }
      return;
    }

    const frame = reel.frames[frameIndex];
    if (!frame || !reel.loaded.has(frameIndex)) {
      this._drawNearest(reel, frameIndex);
      this._setLoading(true);
      return;
    }
    this._setLoading(false);
    this._paint(frame, reel.fit);
  }

  _drawNearest(reel, target) {
    for (let d = 1; d < reel.frameCount; d++) {
      const lo = target - d;
      const hi = target + d;
      if (lo >= 0 && reel.loaded.has(lo)) { this._paint(reel.frames[lo], reel.fit); return; }
      if (hi < reel.frameCount && reel.loaded.has(hi)) { this._paint(reel.frames[hi], reel.fit); return; }
    }
    if (reel.fallbackImg) this._paint(reel.fallbackImg, reel.fit);
  }

  _paint(img, fit) {
    const ctx = this._ctx;
    const cw  = this._cw;
    const ch  = this._ch;
    const iw  = img.naturalWidth;
    const ih  = img.naturalHeight;

    const scale = fit === 'cover'
      ? Math.max(cw / iw, ch / ih)
      : Math.min(cw / iw, ch / ih);
    const dw = iw * scale;
    const dh = ih * scale;

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
  }

  _setLoading(visible) {
    if (this._indicator) this._indicator.hidden = !visible;
  }

  // ── Scroll ────────────────────────────────────────────────────────────────────

  // Fade and rotate run concurrently (not sequentially) on both entry and
  // exit, so the segment's scroll cost is however long the longer of the
  // two takes, not their sum.
  _reelTotalVh(reel) {
    const entryVh = Math.max(reel.fadeInVh, reel.rotateInVh);
    const exitVh  = Math.max(reel.fadeOutVh, reel.rotateOutVh);
    return entryVh + reel.holdVh + exitVh;
  }

  _onScrollRAF() {
    const scrollable = this.offsetHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const scrolledPx = Math.max(0, Math.min(scrollable, -this.getBoundingClientRect().top));

    if (this._progressEl) {
      this._progressEl.style.width = `${(scrolledPx / scrollable) * 100}%`;
    }

    // "Started scrolling yet?" — a fixed px threshold regardless of how long
    // the overall track is, so the hint disappears after one deliberate
    // scroll gesture, not a percentage of a potentially huge total.
    if (this._scrollHint) {
      this._scrollHint.classList.toggle('is-visible', scrolledPx < 120);
    }

    const totalVh = this._reels.reduce((sum, r) => sum + this._reelTotalVh(r), 0) || 1;

    // Which reel does this scroll position fall in?
    let reelIndex = 0;
    let reelLocalPx = 0;
    let reelTrackPx = 0;
    let acc = 0;
    for (let i = 0; i < this._reels.length; i++) {
      const rVh = this._reelTotalVh(this._reels[i]);
      const rPx = scrollable * (rVh / totalVh);
      if (scrolledPx <= acc + rPx || i === this._reels.length - 1) {
        reelIndex = i;
        reelTrackPx = rPx;
        reelLocalPx = Math.max(0, Math.min(rPx, scrolledPx - acc));
        break;
      }
      acc += rPx;
    }

    const reel = this._reels[reelIndex];
    const reelVh = this._reelTotalVh(reel);
    const entryVh = Math.max(reel.fadeInVh, reel.rotateInVh);
    const exitVh  = Math.max(reel.fadeOutVh, reel.rotateOutVh);
    const entryPx = reelTrackPx * (entryVh / reelVh);
    const holdPx  = reelTrackPx * (reel.holdVh / reelVh);
    const exitPx  = reelTrackPx * (exitVh / reelVh);
    const fadeInPx    = reelTrackPx * (reel.fadeInVh / reelVh);
    const rotateInPx  = reelTrackPx * (reel.rotateInVh / reelVh);
    const fadeOutPx   = reelTrackPx * (reel.fadeOutVh / reelVh);
    const rotateOutPx = reelTrackPx * (reel.rotateOutVh / reelVh);

    const b1 = entryPx;
    const b2 = b1 + holdPx;

    let segment, opacity, frameIndex;

    if (reelLocalPx < b1) {
      // Entry — fade-in and rotate-in start together and each run their own
      // duration, so the shorter one (usually the fade) "eats into" the
      // longer one instead of gating it.
      segment = 'entry';
      const fadeP   = fadeInPx > 0 ? Math.min(1, reelLocalPx / fadeInPx) : 1;
      const rotateP = rotateInPx > 0 ? Math.min(1, reelLocalPx / rotateInPx) : 1;
      opacity = fadeP;
      frameIndex = Math.round(rotateP * (reel.frameCount - 1));
    } else if (reelLocalPx <= b2) {
      segment = 'hold';
      opacity = 1;
      frameIndex = reel.frameCount - 1;
    } else {
      // Exit — mirror of entry: fade-out and rotate-out run concurrently,
      // timed to both finish at the end of the reel's track.
      segment = 'exit';
      const exitLocalPx = reelLocalPx - b2;
      const remaining = Math.max(0, exitPx - exitLocalPx);
      const fadeP   = fadeOutPx > 0 ? Math.max(0, Math.min(1, remaining / fadeOutPx)) : 0;
      const rotateP = rotateOutPx > 0 ? Math.min(1, exitLocalPx / rotateOutPx) : 1;
      opacity = fadeP;
      frameIndex = Math.round((1 - rotateP) * (reel.frameCount - 1));
    }

    if (this._sticky) {
      this._sticky.style.opacity = String(Math.max(0, Math.min(1, opacity)));
    }

    if (reelIndex !== this._currentReel || frameIndex !== this._currentFrame) {
      this._currentReel  = reelIndex;
      this._currentFrame = frameIndex;
      this._drawReelFrame(reelIndex, frameIndex);
    }

    this._setActiveCta(segment === 'hold' ? reelIndex : -1);
  }

  _setActiveCta(index) {
    if (index === this._activeCta) return;
    this._activeCta = index;
    this._reelEls.forEach((el, i) => el.classList.toggle('is-active', i === index));
  }
}

customElements.define('dst-showcase-track', DstShowcaseTrack);
